import { useCallback, useEffect, useRef } from "react";
import Hls from "hls.js";

import { usePrevious } from "../../helpers/commonUtils";
import { usePlayerStore } from "../../store/usePlayerStore";

import { setupHls } from "./setupHls";

const useSetupAudio = ({
  audio,
  destroy,
  manifestUrl,
  mediaUrl
}: {
  audio?: HTMLMediaElement;
  destroy?: boolean;
  manifestUrl?: string;
  mediaUrl?: string;
}): void => {
  const { getState, useStoreState } = usePlayerStore();
  const isAutoplay = useStoreState.isAutoplay();
  const isPlaybackStarted = useStoreState.isPlaybackStarted();
  const isPlayerSetupDone = useStoreState.isPlayerSetupDone();
  const player = useStoreState.player();

  const setError = useStoreState.setError();
  const setIsPlayerSetupDone = useStoreState.setIsPlayerSetupDone();

  const hlsRef = useRef<Hls | null>(null);
  const initialStartDone = useRef(false);

  const previousMediaUrl = usePrevious(mediaUrl);
  const previousManifestUrl = usePrevious(manifestUrl);

  useEffect(() => {
    if (
      (manifestUrl && manifestUrl !== previousManifestUrl) ||
      (mediaUrl && mediaUrl !== previousMediaUrl)
    ) {
      setIsPlayerSetupDone(false);
      initialStartDone.current = false;
    }
  }, [
    manifestUrl,
    mediaUrl,
    previousManifestUrl,
    previousMediaUrl,
    setIsPlayerSetupDone
  ]);

  const html5ErrorCallback = useCallback(
    (audioElement: HTMLAudioElement) => () =>
      setError({
        code: audioElement.error?.code,
        message: audioElement.error?.message
      }),
    [setError]
  );

  const setupOnDemandAudio = useCallback(
    (audioElement: HTMLAudioElement, mediaUrlArgument: string) => {
      audioElement.addEventListener("error", html5ErrorCallback(audioElement));
      audioElement.src = mediaUrlArgument;

      if (getState().device.os.name === "iOS") {
        // iOS can change currentTime after canplay event
        // But this does not work for other os
        audioElement.addEventListener(
          "canplay",
          () =>
            (audioElement.currentTime = Math.max(getState().currentTime, 0)),
          { once: true }
        );
      } else {
        audioElement.currentTime = Math.max(getState().currentTime, 0);
      }
    },
    [getState, html5ErrorCallback]
  );

  const setupLiveAudio = useCallback(
    (audioElement: HTMLAudioElement, manifestUrlArgument: string) => {
      if (Hls.isSupported()) {
        // Live audio with m3u8 file type when HLS.js is supported
        hlsRef.current = setupHls({
          manifestUrl: manifestUrlArgument,
          setError
        });
      } else {
        // Try live audio with m3u8 file type natively
        audioElement.addEventListener(
          "error",
          html5ErrorCallback(audioElement)
        );
        audioElement.src = manifestUrlArgument;
      }
    },
    [html5ErrorCallback, setError]
  );

  const doInitialStart = useCallback(() => {
    if (!audio) return;
    initialStartDone.current = true;
    if (manifestUrl && hlsRef.current) {
      hlsRef.current.attachMedia(audio);
    }

    if (isAutoplay) {
      player.autoplay?.();
    }
  }, [audio, isAutoplay, manifestUrl, player]);

  useEffect(() => {
    if (audio && (manifestUrl || mediaUrl) && !isPlayerSetupDone) {
      const originalPlaybackRate = audio.playbackRate;

      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      // On-demand audio with mp3 file type
      if (mediaUrl) {
        setupOnDemandAudio(audio, mediaUrl);
      }

      if (manifestUrl) {
        setupLiveAudio(audio, manifestUrl);
      }

      setIsPlayerSetupDone(true);

      // Playback rate might be reseted to 1 on setup (maybe when setting src)
      audio.playbackRate = originalPlaybackRate;

      if (isAutoplay) {
        doInitialStart();
      }
    }
  }, [
    audio,
    doInitialStart,
    isAutoplay,
    isPlayerSetupDone,
    manifestUrl,
    mediaUrl,
    setIsPlayerSetupDone,
    setupLiveAudio,
    setupOnDemandAudio
  ]);

  useEffect(() => {
    if (isPlayerSetupDone && isPlaybackStarted && !initialStartDone.current) {
      doInitialStart();
    }
  }, [doInitialStart, isPlaybackStarted, isPlayerSetupDone]);

  useEffect(() => {
    if (destroy) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      } else if (audio) {
        audio.src = "";
      }
    }
  }, [audio, destroy]);
};

export default useSetupAudio;
